import {Stack, Typography, useTheme} from '@mui/material';
import {AlertModal, CTAButtonFixed} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import Icon from 'presentation/icons';
import {CustomCTAButtonFixedAction} from 'presentation/pages/private/business-access-permission/styles';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import React from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {BillsTaggingEnum} from 'main/analytics/gaTagsEnum';
import {TitleAndDownloadButtonProps} from './types';

import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants';

const {pdfDownload, close, notice} = translations['pt-br'].commons;
const {moreInformation} = translations['pt-br'].privateFlow.myFinancial.billetDetails;
const {
	openBills: {errorDownloadPdf},
} = translations['pt-br'].privateFlow.myFinancial;

const TitleAndDownloadButton = ({
	onCheckOlderBills,
	downloadLink,
	downloadDataTagId,
	downloadTestId,
	hideWhenNoLink,
	isDownloading,
	isPaid,
	modalErrorState,
	setModalErrorState,
	onDownload,
}: TitleAndDownloadButtonProps) => {
	const {isMobile} = useMediaQueries();
	const queryClient = useQueryClient();
	const {colors} = useTheme();

	const {isEnabled: saoLucasDownloadFlagIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_DOWNLOAD_BILLS_SAO_LUCAS,
	);
	const isSaoLucasBill = !!downloadLink;
	const isSaoLucasDownloadHidden = isSaoLucasBill && !saoLucasDownloadFlagIsEnabled;
	const isHiddenByMergeRule = !!hideWhenNoLink && !downloadLink;
	const shouldShowDownloadButton = !isPaid && !isSaoLucasDownloadHidden && !isHiddenByMergeRule;

	const handleCloseModal = () => {
		setModalErrorState(false);
		queryClient.invalidateQueries({queryKey: [QUERY_KEYS.OBLIGATION_PDF]});
	};

	const handleDownloadClick = () => {
		if (onCheckOlderBills) {
			const hasOlderBills = onCheckOlderBills();
			if (hasOlderBills) return;
		}
		onDownload();
	};

	return (
		<Stack
			flexDirection={'row'}
			justifyContent={'space-between'}
			alignItems={'center'}
			mb={'16px'}>
			<Typography fontWeight={600} color={colors.light.text.secondary}>
				{moreInformation}
			</Typography>
			{shouldShowDownloadButton ? (
				<CTAButtonFixed.Root
					direction={isMobile ? 'column' : 'row'}
					justifyContent={'end'}
					mt={2}>
					<CustomCTAButtonFixedAction
						data-tag-id={downloadDataTagId ?? BillsTaggingEnum.CLICK_BAIXAR_PDF_BOLETO}
						data-testid={downloadTestId}
						fullWidth={isMobile}
						onClick={handleDownloadClick}
						size="medium"
						variant="outlined"
						disabled={isDownloading}
						endIcon={
							isDownloading ? (
								<Loading color="inherit" size={16} />
							) : (
								<Icon name="download" size={16} />
							)
						}>
						{pdfDownload}
					</CustomCTAButtonFixedAction>
				</CTAButtonFixed.Root>
			) : null}
			<AlertModal
				open={modalErrorState}
				onClose={handleCloseModal}
				headerTitle={notice}
				variant="info"
				title={errorDownloadPdf.title}
				description={errorDownloadPdf.description}
				buttonLabel={close}
			/>
		</Stack>
	);
};

export {TitleAndDownloadButton};
